import { CommissionReportData, TechCommissionSummary } from '@/types/techCommissionReport';
import { DEFAULT_TAX_CONFIG, type TaxConfig } from './tax';
import { DEFAULT_COMPANY_LOCALE, formatDocDate, formatMoney, type CompanyLocale } from './locale';
import { DEFAULT_TIMEZONE } from './timezone';

export const generateSingleTechCommissionHtml = (
  techData: TechCommissionSummary,
  startDate: string,
  endDate: string,
  companyName: string,
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
  locale: CompanyLocale = DEFAULT_COMPANY_LOCALE,
  timezone: string = DEFAULT_TIMEZONE,
): string => {
  const incTaxLabel = taxConfig.enabled ? `inc. ${taxConfig.label}` : 'gross';
  const exTaxLabel = taxConfig.enabled ? `ex. ${taxConfig.label}` : 'net';
  const formatCurrency = (amount: number) => formatMoney(amount, locale);

  const formatDate = (dateStr: string) => {
    return formatDocDate(dateStr, locale.shortDate, timezone);
  };

  const formatDateTime = (dateStr: string) => {
    return formatDocDate(dateStr, `${locale.shortDate} HH:mm`, timezone);
  };

  const aggregateOverrideIncomeBySourceTech = () => {
    const bySource = techData.overrideIncomeItems.reduce((acc, item) => {
      const key = item.sourceTechId;
      if (!acc[key]) {
        acc[key] = { sourceTechName: item.sourceTechName, total: 0 };
      }
      acc[key].total += item.overrideAmount;
      return acc;
    }, {} as Record<string, { sourceTechName: string; total: number }>);

    return Object.values(bySource)
      .sort((a, b) => a.sourceTechName.localeCompare(b.sourceTechName));
  };

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${techData.techName} - Commission Report</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; line-height: 1.6; color: #333; background: #fff; padding: 20px; }
    .report-header { text-align: center; margin-bottom: 40px; padding-bottom: 20px; border-bottom: 3px solid #1e3a8a; }
    .report-header h1 { color: #1e3a8a; font-size: 32px; margin-bottom: 10px; }
    .report-header .period { font-size: 18px; color: #666; margin-bottom: 5px; }
    .report-header .generated { font-size: 14px; color: #999; }
    .tech-summary { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 30px; border-radius: 10px; margin-bottom: 40px; }
    .tech-summary h2 { margin-bottom: 20px; font-size: 24px; }
    .summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; }
    .summary-item { background: rgba(255, 255, 255, 0.1); padding: 15px; border-radius: 8px; }
    .summary-item .label { font-size: 14px; opacity: 0.9; margin-bottom: 5px; }
    .summary-item .value { font-size: 28px; font-weight: bold; }
    h3 { color: #1e3a8a; margin: 30px 0 15px 0; font-size: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; }
    table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 14px; }
    .data-table { box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    .data-table th { background: #1e3a8a; color: white; font-weight: 600; text-align: left; padding: 12px 8px; }
    .data-table td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
    .data-table tbody tr:hover { background: #f8fafc; }
    .data-table tbody tr:nth-child(even) { background: #fafafa; }
    .summary-table td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; }
    .summary-table td:first-child { width: 200px; font-weight: 500; }
    .highlight { background: #fef3c7 !important; }
    .highlight-col { background: #fef3c7; font-weight: bold; font-size: 15px; }
    .negative { color: #dc2626; font-weight: bold; }
    .negative-row { background: #fee2e2 !important; }
    .tech-part-row { background: #dbeafe !important; }
    .totals-row { background: #1e3a8a !important; color: white; font-weight: bold; }
    .totals-row td { padding: 12px 8px; border-bottom: none; }
    @media print { body { padding: 0; } .data-table { box-shadow: none; } }
    @media screen and (max-width: 1200px) { .data-table { font-size: 12px; } .data-table th, .data-table td { padding: 8px 4px; } }
  </style>
</head>
<body>
  <div class="report-header">
    <h1>${companyName}</h1>
    <h1>${techData.techName} - Commission Report</h1>
    <div class="period">Period: ${formatDate(startDate)} - ${formatDate(endDate)}</div>
    <div class="generated">Generated: ${formatDateTime(new Date().toISOString())}</div>
  </div>
  
  <div class="tech-summary">
    <h2>Summary - Commission Rate: ${techData.commissionRate}%</h2>
    <div class="summary-grid">
      <div class="summary-item">
        <div class="label">Jobs Completed</div>
        <div class="value">${techData.totalJobs}</div>
      </div>
      <div class="summary-item">
        <div class="label">Total Revenue (${exTaxLabel})</div>
        <div class="value">${formatCurrency(techData.totalRevenue)}</div>
      </div>
      <div class="summary-item">
        <div class="label">Job Commission</div>
        <div class="value">${formatCurrency(techData.totalGrossCommission)}</div>
      </div>
      <div class="summary-item">
        <div class="label">Total Parts</div>
        <div class="value">${formatCurrency(techData.totalPartsCost)}</div>
      </div>
      <div class="summary-item" style="background: rgba(255, 255, 255, 0.2);">
        <div class="label">Commission + Parts Total</div>
        <div class="value">${formatCurrency(techData.totalGrossCommission + techData.totalPartsCost)}</div>
      </div>
      <div class="summary-item">
        <div class="label">Cash Collected</div>
        <div class="value">${formatCurrency(techData.totalCashCollected)}</div>
      </div>
      <div class="summary-item">
        <div class="label">Tech Parts Reimbursement</div>
        <div class="value">${formatCurrency(techData.totalTechPartsReimbursement)}</div>
      </div>
      ${techData.totalOverrideIncome > 0 ? `
      <div class="summary-item" style="background: rgba(34, 197, 94, 0.2);">
        <div class="label">Override Income</div>
        <div class="value">${formatCurrency(techData.totalOverrideIncome)}</div>
      </div>
      ` : ''}
      <div class="summary-item" style="background: rgba(255, 255, 255, 0.3);">
        <div class="label">Total to Tech</div>
        <div class="value ${techData.totalFinalCommission < 0 ? 'negative' : ''}">${formatCurrency(techData.totalFinalCommission)}</div>
      </div>
    </div>
    ${techData.totalFinalCommission < 0 ? '<p style="margin-top: 15px; font-size: 16px; background: rgba(255,255,255,0.2); padding: 10px; border-radius: 5px;">⚠️ Negative commission: Technician owes company money this period</p>' : ''}
  </div>
  
  ${techData.overrideIncomeItems.length > 0 ? `
  <h3>Override Income Received</h3>
  <p style="color: #666; margin-bottom: 15px;">Commission earned from other technicians' completed jobs</p>
  <table class="data-table">
    <thead>
      <tr>
        <th>Source Technician</th>
        <th class="highlight-col">Total Override</th>
      </tr>
    </thead>
    <tbody>
      ${aggregateOverrideIncomeBySourceTech().map(row => `
        <tr>
          <td>${row.sourceTechName}</td>
          <td class="highlight-col">${formatCurrency(row.total)}</td>
        </tr>
      `).join('')}
    </tbody>
    <tfoot>
      <tr class="totals-row">
        <td><strong>TOTAL OVERRIDE INCOME</strong></td>
        <td class="highlight-col"><strong>${formatCurrency(techData.totalOverrideIncome)}</strong></td>
      </tr>
    </tfoot>
  </table>
  ` : ''}
  </div>
  
  <h3>Job Breakdown</h3>
  <table class="data-table">
    <thead>
      <tr>
        <th>Job #</th>
        <th>Customer</th>
        <th>Date</th>
        <th>Rev (${incTaxLabel})</th>
        <th>Rev (${exTaxLabel})</th>
        <th>Parts</th>
        <th>Tolls</th>
        <th>Card Fee</th>
        <th>Net Profit</th>
        <th>Gross Comm</th>
        <th>Cash</th>
        <th>Tech Parts</th>
        <th class="highlight-col">Final Comm</th>
      </tr>
    </thead>
    <tbody>
      ${techData.jobs.map(job => `
        <tr ${job.finalCommission < 0 ? 'class="negative-row"' : ''}>
          <td>${job.jobNumber}</td>
          <td>${job.customerName}</td>
          <td>${formatDate(job.closedDate)}</td>
          <td>${formatCurrency(job.revenueIncGst)}</td>
          <td>${formatCurrency(job.revenueExGst)}</td>
          <td>${formatCurrency(job.totalPartsCost)}</td>
          <td>${formatCurrency(job.tollsCost)}</td>
          <td>${formatCurrency(job.cardProcessingFee)}</td>
          <td>${formatCurrency(job.netProfit)}</td>
          <td>${formatCurrency(job.grossCommission)}</td>
          <td>${formatCurrency(job.cashCollected)}</td>
          <td>${formatCurrency(job.techPartsCost)}</td>
          <td class="highlight-col ${job.finalCommission < 0 ? 'negative' : ''}">${formatCurrency(job.finalCommission)}</td>
        </tr>
      `).join('')}
    </tbody>
    <tfoot>
      <tr class="totals-row">
        <td colspan="3"><strong>TOTALS</strong></td>
        <td><strong>${formatCurrency(techData.jobs.reduce((sum, j) => sum + j.revenueIncGst, 0))}</strong></td>
        <td><strong>${formatCurrency(techData.totalRevenue)}</strong></td>
        <td><strong>${formatCurrency(techData.jobs.reduce((sum, j) => sum + j.totalPartsCost, 0))}</strong></td>
        <td><strong>${formatCurrency(techData.jobs.reduce((sum, j) => sum + j.tollsCost, 0))}</strong></td>
        <td><strong>${formatCurrency(techData.totalCardFees)}</strong></td>
        <td><strong>${formatCurrency(techData.totalNetProfit)}</strong></td>
        <td><strong>${formatCurrency(techData.totalGrossCommission)}</strong></td>
        <td><strong>${formatCurrency(techData.totalCashCollected)}</strong></td>
        <td><strong>${formatCurrency(techData.totalTechPartsReimbursement)}</strong></td>
        <td class="highlight-col"><strong>${formatCurrency(techData.totalFinalCommission)}</strong></td>
      </tr>
    </tfoot>
  </table>
  
  <h3>Parts Breakdown</h3>
  <table class="data-table">
    <thead>
      <tr>
        <th>Part Name</th>
        <th>Total Qty</th>
        <th>Total Cost</th>
        <th>Source</th>
      </tr>
    </thead>
    <tbody>
      ${techData.allPartsUsed.length > 0 ? techData.allPartsUsed.map(part => `
        <tr ${part.source === 'tech_personal' ? 'class="tech-part-row"' : ''}>
          <td>${part.partName}</td>
          <td>${part.totalQuantity}</td>
          <td>${formatCurrency(part.totalCost)}</td>
          <td>${part.source === 'tech_personal' ? '🔧 Tech Personal' : '📦 Company Stock'}</td>
        </tr>
      `).join('') : '<tr><td colspan="4" style="text-align: center; color: #666;">No parts used</td></tr>'}
    </tbody>
  </table>
  
  <h3>Payment Methods Summary</h3>
  <table class="summary-table">
    <tr>
      <td><strong>Cash:</strong></td>
      <td>${formatCurrency(techData.paymentSummary.cash)}</td>
    </tr>
    <tr>
      <td><strong>Card/EFTPOS:</strong></td>
      <td>${formatCurrency(techData.paymentSummary.card)}</td>
    </tr>
    <tr>
      <td><strong>Bank Transfer:</strong></td>
      <td>${formatCurrency(techData.paymentSummary.bankTransfer)}</td>
    </tr>
    <tr class="highlight">
      <td><strong>Total Payments:</strong></td>
      <td><strong>${formatCurrency(techData.paymentSummary.total)}</strong></td>
    </tr>
  </table>
  
  <div style="text-align: center; color: #999; padding: 20px; border-top: 2px solid #e2e8f0; margin-top: 40px;">
    <p>End of Report</p>
    <p style="font-size: 12px;">This is a computer-generated document. No signature required.</p>
  </div>
</body>
</html>
  `.trim();
};

export const generateTechCommissionHtml = (
  reportData: CommissionReportData,
  taxConfig: TaxConfig = DEFAULT_TAX_CONFIG,
  locale: CompanyLocale = DEFAULT_COMPANY_LOCALE,
  timezone: string = DEFAULT_TIMEZONE,
): string => {
  const incTaxLabel = taxConfig.enabled ? `inc. ${taxConfig.label}` : 'gross';
  const exTaxLabel = taxConfig.enabled ? `ex. ${taxConfig.label}` : 'net';
  const formatCurrency = (amount: number) => formatMoney(amount, locale);

  const formatDate = (dateStr: string) => {
    return formatDocDate(dateStr, locale.shortDate, timezone);
  };

  const formatDateTime = (dateStr: string) => {
    return formatDocDate(dateStr, `${locale.shortDate} HH:mm`, timezone);
  };

  const aggregateOverrideIncomeBySourceTech = (tech: TechCommissionSummary) => {
    const bySource = tech.overrideIncomeItems.reduce((acc, item) => {
      const key = item.sourceTechId;
      if (!acc[key]) {
        acc[key] = { sourceTechName: item.sourceTechName, total: 0 };
      }
      acc[key].total += item.overrideAmount;
      return acc;
    }, {} as Record<string, { sourceTechName: string; total: number }>);

    return Object.values(bySource)
      .sort((a, b) => a.sourceTechName.localeCompare(b.sourceTechName));
  };

  // Generate technician sections
  const techSectionsHtml = reportData.technicians.map(tech => `
    <div class="tech-section">
      <h2 class="tech-header">${tech.techName} - Commission Rate: ${tech.commissionRate}%</h2>
      
      <div class="summary-box">
        <table class="summary-table">
          <tr>
            <td><strong>Jobs Completed:</strong></td>
            <td>${tech.totalJobs}</td>
            <td><strong>Total Revenue (${exTaxLabel}):</strong></td>
            <td>${formatCurrency(tech.totalRevenue)}</td>
          </tr>
          <tr>
            <td><strong>Job Commission:</strong></td>
            <td>${formatCurrency(tech.totalGrossCommission)}</td>
            <td><strong>Total Parts:</strong></td>
            <td>${formatCurrency(tech.totalPartsCost)}</td>
          </tr>
          <tr>
            <td><strong>Commission + Parts Total:</strong></td>
            <td>${formatCurrency(tech.totalGrossCommission + tech.totalPartsCost)}</td>
            <td><strong>Cash Collected:</strong></td>
            <td>${formatCurrency(tech.totalCashCollected)}</td>
          </tr>
          <tr>
            <td><strong>Tech Parts Reimbursement:</strong></td>
            <td>${formatCurrency(tech.totalTechPartsReimbursement)}</td>
            <td><strong>Total to Tech:</strong></td>
            <td>${formatCurrency(tech.totalFinalCommission)}${tech.totalFinalCommission < 0 ? ' <span class="negative">(Tech owes company)</span>' : ''}</td>
          </tr>
          ${tech.totalOverrideIncome > 0 ? `
          <tr style="background: #dcfce7;">
            <td><strong>Override Income:</strong></td>
            <td>${formatCurrency(tech.totalOverrideIncome)}</td>
            <td colspan="2" style="color: #666; font-size: 12px;">Commission from other techs' jobs</td>
          </tr>
          ` : ''}
          <tr class="highlight">
            <td><strong>Grand Total Earnings:</strong></td>
            <td colspan="3" class="highlight"><strong>${formatCurrency(tech.grandTotalEarnings)}</strong></td>
          </tr>
        </table>
      </div>
      
      ${tech.overrideIncomeItems.length > 0 ? `
      <h3>Override Income Received</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Source Technician</th>
            <th class="highlight-col">Total Override</th>
          </tr>
        </thead>
        <tbody>
          ${aggregateOverrideIncomeBySourceTech(tech).map(row => `
            <tr>
              <td>${row.sourceTechName}</td>
              <td class="highlight-col">${formatCurrency(row.total)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr class="totals-row">
            <td><strong>TOTAL OVERRIDE INCOME</strong></td>
            <td class="highlight-col"><strong>${formatCurrency(tech.totalOverrideIncome)}</strong></td>
          </tr>
        </tfoot>
      </table>
      ` : ''}
      
      ${tech.jobs.length > 0 ? `
      <h3>Job Breakdown</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Job #</th>
            <th>Customer</th>
            <th>Date</th>
            <th>Rev (${incTaxLabel})</th>
            <th>Rev (${exTaxLabel})</th>
            <th>Parts</th>
            <th>Tolls</th>
            <th>Card Fee</th>
            <th>Net Profit</th>
            <th>Gross Comm</th>
            <th>Cash</th>
            <th>Tech Parts</th>
            <th class="highlight-col">Final Comm</th>
          </tr>
        </thead>
        <tbody>
          ${tech.jobs.map(job => `
            <tr ${job.finalCommission < 0 ? 'class="negative-row"' : ''}>
              <td>${job.jobNumber}</td>
              <td>${job.customerName}</td>
              <td>${formatDate(job.closedDate)}</td>
              <td>${formatCurrency(job.revenueIncGst)}</td>
              <td>${formatCurrency(job.revenueExGst)}</td>
              <td>${formatCurrency(job.totalPartsCost)}</td>
              <td>${formatCurrency(job.tollsCost)}</td>
              <td>${formatCurrency(job.cardProcessingFee)}</td>
              <td>${formatCurrency(job.netProfit)}</td>
              <td>${formatCurrency(job.grossCommission)}</td>
              <td>${formatCurrency(job.cashCollected)}</td>
              <td>${formatCurrency(job.techPartsCost)}</td>
              <td class="highlight-col ${job.finalCommission < 0 ? 'negative' : ''}">${formatCurrency(job.finalCommission)}</td>
            </tr>
          `).join('')}
        </tbody>
        <tfoot>
          <tr class="totals-row">
            <td colspan="3"><strong>TOTALS</strong></td>
            <td><strong>${formatCurrency(tech.jobs.reduce((sum, j) => sum + j.revenueIncGst, 0))}</strong></td>
            <td><strong>${formatCurrency(tech.totalRevenue)}</strong></td>
            <td><strong>${formatCurrency(tech.jobs.reduce((sum, j) => sum + j.totalPartsCost, 0))}</strong></td>
            <td><strong>${formatCurrency(tech.jobs.reduce((sum, j) => sum + j.tollsCost, 0))}</strong></td>
            <td><strong>${formatCurrency(tech.totalCardFees)}</strong></td>
            <td><strong>${formatCurrency(tech.totalNetProfit)}</strong></td>
            <td><strong>${formatCurrency(tech.totalGrossCommission)}</strong></td>
            <td><strong>${formatCurrency(tech.totalCashCollected)}</strong></td>
            <td><strong>${formatCurrency(tech.totalTechPartsReimbursement)}</strong></td>
            <td class="highlight-col"><strong>${formatCurrency(tech.totalFinalCommission)}</strong></td>
          </tr>
        </tfoot>
      </table>
      ` : '<p style="color: #666; padding: 20px; background: #f8f8f8; border-radius: 8px; text-align: center;">No jobs completed in this period (override income only)</p>'}
      
      ${tech.allPartsUsed.length > 0 ? `
      <h3>Parts Breakdown</h3>
      <table class="data-table">
        <thead>
          <tr>
            <th>Part Name</th>
            <th>Total Qty</th>
            <th>Total Cost</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          ${tech.allPartsUsed.map(part => `
            <tr ${part.source === 'tech_personal' ? 'class="tech-part-row"' : ''}>
              <td>${part.partName}</td>
              <td>${part.totalQuantity}</td>
              <td>${formatCurrency(part.totalCost)}</td>
              <td>${part.source === 'tech_personal' ? '🔧 Tech Personal' : '📦 Company Stock'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ` : ''}
      
      ${tech.paymentSummary.total > 0 ? `
      <h3>Payment Methods Summary</h3>
      <table class="summary-table">
        <tr>
          <td><strong>Cash:</strong></td>
          <td>${formatCurrency(tech.paymentSummary.cash)}</td>
        </tr>
        <tr>
          <td><strong>Card/EFTPOS:</strong></td>
          <td>${formatCurrency(tech.paymentSummary.card)}</td>
        </tr>
        <tr>
          <td><strong>Bank Transfer:</strong></td>
          <td>${formatCurrency(tech.paymentSummary.bankTransfer)}</td>
        </tr>
        <tr class="highlight">
          <td><strong>Total Payments:</strong></td>
          <td><strong>${formatCurrency(tech.paymentSummary.total)}</strong></td>
        </tr>
      </table>
      ` : ''}
    </div>
  `).join('');

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Technician Commission Report - ${formatDate(reportData.startDate)} to ${formatDate(reportData.endDate)}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background: #fff;
      padding: 20px;
    }
    
    .report-header {
      text-align: center;
      margin-bottom: 40px;
      padding-bottom: 20px;
      border-bottom: 3px solid #1e3a8a;
    }
    
    .report-header h1 {
      color: #1e3a8a;
      font-size: 32px;
      margin-bottom: 10px;
    }
    
    .report-header .period {
      font-size: 18px;
      color: #666;
      margin-bottom: 5px;
    }
    
    .report-header .generated {
      font-size: 14px;
      color: #999;
    }
    
    .overall-summary {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 30px;
      border-radius: 10px;
      margin-bottom: 40px;
    }
    
    .overall-summary h2 {
      margin-bottom: 20px;
      font-size: 24px;
    }
    
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 20px;
    }
    
    .summary-item {
      background: rgba(255, 255, 255, 0.1);
      padding: 15px;
      border-radius: 8px;
    }
    
    .summary-item .label {
      font-size: 14px;
      opacity: 0.9;
      margin-bottom: 5px;
    }
    
    .summary-item .value {
      font-size: 28px;
      font-weight: bold;
    }
    
    .tech-section {
      page-break-after: always;
      margin-bottom: 60px;
    }
    
    .tech-header {
      background: #1e3a8a;
      color: white;
      padding: 15px 20px;
      border-radius: 8px 8px 0 0;
      font-size: 24px;
      margin-bottom: 0;
    }
    
    .summary-box {
      background: #f8fafc;
      padding: 20px;
      border-radius: 0 0 8px 8px;
      margin-bottom: 30px;
      border: 1px solid #e2e8f0;
      border-top: none;
    }
    
    h3 {
      color: #1e3a8a;
      margin: 30px 0 15px 0;
      font-size: 20px;
      border-bottom: 2px solid #e2e8f0;
      padding-bottom: 8px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
      font-size: 14px;
    }
    
    .data-table {
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    }
    
    .data-table th {
      background: #1e3a8a;
      color: white;
      font-weight: 600;
      text-align: left;
      padding: 12px 8px;
      position: sticky;
      top: 0;
    }
    
    .data-table td {
      padding: 10px 8px;
      border-bottom: 1px solid #e2e8f0;
    }
    
    .data-table tbody tr:hover {
      background: #f8fafc;
    }
    
    .data-table tbody tr:nth-child(even) {
      background: #fafafa;
    }
    
    .summary-table td {
      padding: 8px 12px;
      border-bottom: 1px solid #e2e8f0;
    }
    
    .summary-table td:first-child {
      width: 200px;
      font-weight: 500;
    }
    
    .highlight {
      background: #fef3c7 !important;
    }
    
    .highlight-col {
      background: #fef3c7;
      font-weight: bold;
      font-size: 15px;
    }
    
    .negative {
      color: #dc2626;
      font-weight: bold;
    }
    
    .negative-row {
      background: #fee2e2 !important;
    }
    
    .tech-part-row {
      background: #dbeafe !important;
    }
    
    .totals-row {
      background: #1e3a8a !important;
      color: white;
      font-weight: bold;
    }
    
    .totals-row td {
      padding: 12px 8px;
      border-bottom: none;
    }
    
    @media print {
      body {
        padding: 0;
      }
      
      .tech-section {
        page-break-after: always;
      }
      
      .data-table {
        box-shadow: none;
      }
    }
    
    @media screen and (max-width: 1200px) {
      .data-table {
        font-size: 12px;
      }
      
      .data-table th,
      .data-table td {
        padding: 8px 4px;
      }
    }
  </style>
</head>
<body>
  <div class="report-header">
    <h1>${reportData.companyName}</h1>
    <h1>Technician Commission Report</h1>
    <div class="period">Period: ${formatDate(reportData.startDate)} - ${formatDate(reportData.endDate)}</div>
    <div class="generated">Generated: ${formatDateTime(reportData.generatedAt)}</div>
  </div>
  
  <div class="overall-summary">
    <h2>Report Summary</h2>
    <div class="summary-grid">
      <div class="summary-item">
        <div class="label">Total Technicians</div>
        <div class="value">${reportData.overallTotals.totalTechnicians}</div>
      </div>
      <div class="summary-item">
        <div class="label">Total Jobs Completed</div>
        <div class="value">${reportData.overallTotals.totalJobs}</div>
      </div>
      <div class="summary-item">
        <div class="label">Total Revenue (${exTaxLabel})</div>
        <div class="value">${formatCurrency(reportData.overallTotals.totalRevenue)}</div>
      </div>
      <div class="summary-item">
        <div class="label">Total Commission Paid</div>
        <div class="value">${formatCurrency(reportData.overallTotals.totalCommissionPaid)}</div>
      </div>
    </div>
  </div>
  
  ${techSectionsHtml}
  
  <div style="text-align: center; color: #999; padding: 20px; border-top: 2px solid #e2e8f0; margin-top: 40px;">
    <p>End of Report</p>
    <p style="font-size: 12px;">This is a computer-generated document. No signature required.</p>
  </div>
</body>
</html>
  `.trim();
};

export const downloadHtmlReport = (html: string, filename: string) => {
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
